import React, { Component, ComponentType } from 'react'
import { Box, BoxProps, styled, TextField, TextFieldProps, Typography, TypographyProps } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { getErrorMessage } from './helpers'
import { tryParseCsvFileToArray } from '../utils'
import { IFormBase, IFormInputBase } from './types'

export interface IInputFileCsvLocalParserSlots {
  /**
   * The URL of the sample CSV file that users can download.
   * If provided, a "Download sample" link will be displayed.
   * Example: '/sample.csv' or 'https://example.com/sample.csv'
   */
  downloadSampleUrl?: string
  /**
   * Instructional text displayed alongside the download link or file input.
   * Useful for guiding users on how to use or fill out the CSV file.
   */
  instructionalText?: string
  /**
   * Props to customize the TextField used for uploading the CSV file.
   * Allows customization of appearance and behavior (e.g., placeholder, onChange, disabled).
   */
  textFieldProps?: TextFieldProps
  /**
   * Props passed to the wrapper element (typically a Box).
   * Useful for customizing layout styles such as margin, padding, or display.
   */
  wrapProps?: BoxProps
  /**
   * Whether the label should shrink when displayed.
   * Defaults to `true` if a label is present to prevent overlap.
   * @default true
   */
  labelShrink?: boolean
}

export interface IInputFileCsvLocalParserParams extends IInputFileCsvLocalParserSlots {}

export interface IInputFileCsvLocalParserProps<T, C> extends IFormInputBase<T, IInputFileCsvLocalParserSlots> {
  onChange?: (value: C[]) => void
}

export interface IInputFileCsvLocalParserState<C> {
  parsedData: C[]
}

type IPickConfigs<T> = Required<Pick<IFormBase<T>, 'onBlur' | 'messageErrors'>>

interface IMergeConfigs<T> extends IPickConfigs<T>, IInputFileCsvLocalParserSlots {
  name: keyof T
}

/**
 * Creates a React component that renders a file input specifically for CSV files,
 * parses the selected file locally into a structured array, and integrates with a form system.
 *
 * @template T - The main data model used in the form. Represents the shape of the overall form data.
 * @template C - The type of each individual item parsed from the CSV file.
 *
 * @param params - Optional slot parameters to customize the appearance or behavior of the text field.
 * @returns A React component configured for uploading and parsing local CSV files.
 */
export function createInputFileCsvLocalParser<T, C>(params?: IInputFileCsvLocalParserParams): ComponentType<IInputFileCsvLocalParserProps<T, C>> {
  class InputFileCsvLocalParser extends Component<IInputFileCsvLocalParserProps<T, C>, IInputFileCsvLocalParserState<C>> {
    private mergeConfigsCache: IMergeConfigs<T> = { name: '' as keyof T, onBlur: () => {}, messageErrors: {} }
    constructor(props: IInputFileCsvLocalParserProps<T, C>) {
      super(props)
      this.mergeConfigsCache = this.upgradeMergeConfigs(props)
      this.state = { parsedData: [] }
    }

    get mergeConfigs(): IMergeConfigs<T> {
      return this.mergeConfigsCache
    }

    get inputValue() {
      return this.state.parsedData.length > 0 ? JSON.stringify(this.state.parsedData) : ''
    }

    shouldComponentUpdate(nextProps: Readonly<IInputFileCsvLocalParserProps<T, C>>): boolean {
      if (nextProps !== this.props) this.mergeConfigsCache = this.upgradeMergeConfigs(nextProps)
      return true
    }

    refInput: HTMLInputElement | null = null
    refTextFiled: HTMLInputElement | null = null
    render() {
      return (
        <Box {...this.mergeConfigs.wrapProps}>
          {this.mergeConfigs.downloadSampleUrl && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
              <Typography variant='subtitle2' component='span'>
                {this.mergeConfigs.instructionalText ?? 'Metadata file, please fill out the form we provide.'}
              </Typography>
              <DownloadButton href={this.mergeConfigs.downloadSampleUrl}>
                <span> Download sample</span>
                <DownloadIcon fontSize='small' />
              </DownloadButton>
            </Box>
          )}
          <input hidden name={this.props.name?.toString()} type='text' ref={(ref) => (this.refInput = ref)} defaultValue='' />
          <TextField {...this.getTextFieldProps()} inputRef={(ref) => (this.refTextFiled = ref)} />
        </Box>
      )
    }

    getTextFieldProps = (): TextFieldProps => {
      const { label } = this.props
      const eMessage = getErrorMessage(this.mergeConfigs.messageErrors, this.mergeConfigs.name)
      const mergedProps: TextFieldProps = {
        label,
        error: eMessage.error,
        helperText: eMessage.message,
        type: 'file',
        variant: 'outlined',
        fullWidth: true,
        onChange: this.handleChange,
        inputProps: { accept: 'text/csv' },
        InputLabelProps: { shrink: this.mergeConfigs.labelShrink },
        ...this.mergeConfigs.textFieldProps
      }
      return mergedProps
    }

    upgradeMergeConfigs = (currentProps: IInputFileCsvLocalParserProps<T, C>): IMergeConfigs<T> => {
      const { name, label, onBlur, messageErrors } = currentProps
      return {
        textFieldProps: Object.assign({}, params?.textFieldProps, currentProps.slots?.textFieldProps),
        wrapProps: Object.assign({}, params?.wrapProps, currentProps.slots?.wrapProps),
        name: name as keyof T,
        onBlur: onBlur || (() => {}),
        messageErrors: messageErrors || {},
        downloadSampleUrl: currentProps.slots?.downloadSampleUrl ?? params?.downloadSampleUrl,
        instructionalText: currentProps.slots?.instructionalText ?? params?.instructionalText,
        labelShrink: (currentProps.slots?.labelShrink ?? params?.labelShrink) !== false && !!label
      }
    }

    handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      try {
        const result = await tryParseCsvFileToArray(file)
        if (this.refInput) this.refInput.value = JSON.stringify(result)
        this.setState({ parsedData: result as C[] }, () => {
          this.props.onChange && this.props.onChange(result as C[])
          this.mergeConfigs.onBlur(this.mergeConfigs.name)
        })
      } catch (err) {
        console.error('Error parsing CSV:', err)
        this.mergeConfigs.onBlur(this.mergeConfigs.name)
      }
    }
  }
  return InputFileCsvLocalParser
}

const DownloadButton = styled((p: TypographyProps & { href: string }) => (
  <Typography variant='subtitle2' component='a' target='_blank' download {...p} />
))(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  color: theme.palette.primary.light,
  cursor: 'pointer',
  fontWeight: 600,
  marginLeft: '6px',
  gap: '4px',
  transition: 'all 0.3s ease',
  '&:hover': {
    textDecoration: 'underline',
    color: theme.palette.primary.dark
  }
}))
